// src/pages/TranslationVerificationPage.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Heading,
    Text,
    Button,
    Textarea,
    Flex,
    Spinner,
    useToast,
    Badge,
    VStack,
    HStack,
    Divider,
    Card,
    CardBody,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    Tabs, TabList, TabPanels, Tab, TabPanel
} from '@chakra-ui/react';
import { CheckIcon, CloseIcon, SearchIcon, RepeatClockIcon, WarningTwoIcon } from '@chakra-ui/icons';
import Sidebar from '../../components/Dashboard/Sidebar';
import { fetchAllSurahs } from '../../api/quranService';
import { fetchTranslationInfo, fetchTranslation, verifyTranslation, deleteVerification } from '../../api/translationVerificationService';
import { useAuth } from '../../context/AuthContext';

// Individual Translation Card Component
const TranslationCard = ({ ayah, selectedTranslationId, onVerify, onDelete, verifying, user }) => {
    const [comment, setComment] = useState('');
    const [correction, setCorrection] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleVerify = async (status) => {
        setIsSubmitting(true);
        try {
            await onVerify(ayah.ayah_key, status, comment, correction);
            // Clear form after successful verification
            setComment('');
            setCorrection('');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        setIsSubmitting(true);
        try {
            await onDelete(ayah.ayah_key);
        } finally {
            setIsSubmitting(false);
        }
    };

    const isCurrentlyVerifying = verifying === ayah.ayah_key || isSubmitting;

    return (
        <Card border="1px" borderColor="gray.200">
            <CardBody>
                <VStack align="stretch" spacing={4}>
                    <HStack justify="space-between">
                        <Text fontWeight="bold" fontSize="lg">Ayah {ayah.ayah_index}</Text>
                        {ayah.user_verification ? (
                            (ayah.user_verification.corrections || ayah.user_verification.comments) ?
                                <Badge colorScheme="yellow">Verified with Correction</Badge> :
                                <Badge colorScheme="green">Verified</Badge>
                        ) : (
                            <Badge colorScheme="yellow">Pending</Badge>
                        )}
                    </HStack>

                    <Text fontSize="lg">
                        {ayah?.text || 'No translation available'}
                    </Text>
                    {!ayah.user_verification && (
                        <HStack justifyContent="flex-end" spacing={4} w="100%">
                            <Button
                                colorScheme="green"
                                leftIcon={<CheckIcon />}
                                onClick={() => handleVerify(true)}
                                isLoading={isCurrentlyVerifying}
                                disabled={correction || comment}
                            >
                                Verify
                            </Button>
                        </HStack>
                    )}

                    {ayah.user_verification?.corrections && (
                        <Text fontSize="l" color="grey">
                            <b>Correction:</b> {ayah.user_verification.corrections}
                        </Text>
                    )}

                    {!ayah.user_verification ? (
                        // Pending verification form
                        <>
                            <Box>
                                <Text mb={2}>Correction (optional):</Text>
                                <Textarea
                                    value={correction}
                                    onChange={(e) => setCorrection(e.target.value)}
                                    placeholder="Write your corrections..."
                                    isDisabled={isCurrentlyVerifying}
                                />
                            </Box>
                            <Box>
                                <Text mb={2}>Expert Comment (optional):</Text>
                                <Textarea
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    placeholder="Write your thoughts..."
                                    isDisabled={isCurrentlyVerifying}
                                />
                            </Box>
                            <HStack justifyContent="flex-end" spacing={4} w="100%">
                                <Button
                                    colorScheme="yellow"
                                    leftIcon={<WarningTwoIcon />}
                                    onClick={() => handleVerify(false)}
                                    isLoading={isCurrentlyVerifying}
                                    disabled={!correction && !comment}
                                >
                                    Verify with Correction
                                </Button>
                            </HStack>
                        </>
                    ) : (
                        // Already verified - show revoke option
                        <HStack justifyContent="flex-end" spacing={4} w="100%">
                            <Badge colorScheme="blue">Verified by : {ayah.user_verification?.username || 'Unknown'}</Badge>
                            <Button
                                colorScheme="blue"
                                leftIcon={<RepeatClockIcon />}
                                onClick={handleDelete}
                                isLoading={isCurrentlyVerifying}
                                disabled={user.userId != ayah.user_verification?.user_id}
                            >
                                Revoke
                            </Button>
                        </HStack>
                    )}
                </VStack>
            </CardBody>
        </Card>
    );
};

const TranslationVerificationPage = () => {
    
    const { user } = useAuth();

    const toast = useToast();

    const [surahs, setSurahs] = useState([]);
    const [selectedSurahId, setSelectedSurahId] = useState('');
    const [filteredSurahs, setFilteredSurahs] = useState([]);
    const [translationInfos, setTranslationInfos] = useState([]);
    const [selectedTranslationId, setSelectedTranslationId] = useState('1');
    const [searchTerm, setSearchTerm] = useState('');

    const [ayahs, setAyahs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(null);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    const toggleSidebar = () => setSidebarCollapsed(!sidebarCollapsed);

    // Load all surahs once
    useEffect(() => {
        const loadSurahs = async () => {
            setLoading(true);
            try {
                const [surahsData, translationData] = await Promise.all([
                    fetchAllSurahs(),
                    fetchTranslationInfo(),
                ]);

                if (surahsData) {
                    setSurahs(surahsData?.surahs);
                    setFilteredSurahs(surahsData?.surahs);
                }

                if (translationData) {
                    setTranslationInfos(translationData);
                }
            } catch (err) {
                toast({
                    title: 'Failed to load surahs',
                    description: err.message || 'Something went wrong',
                    status: 'error',
                    duration: 4000,
                    isClosable: true,
                });
            } finally {
                setLoading(false);
            }
        };

        loadSurahs();
    }, []);

    // Fetch ayahs on surah selection
    useEffect(() => {
        if (!selectedSurahId) return;
        const fetchSurahAyahs = async () => {
            setLoading(true);
            try {
                const data = await fetchTranslation(selectedSurahId, selectedTranslationId);
                setAyahs(data.ayahs);
            } catch (err) {
                toast({
                    title: 'Error fetching ayahs',
                    description: err.message,
                    status: 'error',
                    duration: 4000,
                    isClosable: true
                });
            } finally {
                setLoading(false);
            }
        };
        fetchSurahAyahs();
    }, [selectedSurahId, selectedTranslationId]);

    const handleVerify = async (ayahKey, status, comment, correction) => {
        setVerifying(ayahKey);
        try {
            await verifyTranslation(ayahKey, selectedTranslationId, status, comment, correction);
            toast({
                title: 'Verification submitted',
                status: 'success',
                duration: 3000,
                isClosable: true
            });
            const updated = await fetchTranslation(selectedSurahId, selectedTranslationId);
            setAyahs(updated.ayahs);
        } catch (err) {
            toast({
                title: 'Verification failed',
                description: err.message,
                status: 'error',
                duration: 4000,
                isClosable: true
            });
            throw err; // Re-throw to let the individual component handle it
        } finally {
            setVerifying(null);
        }
    };

    const handleDelete = async (ayahKey) => {
        setVerifying(ayahKey);
        try {
            await deleteVerification(ayahKey, selectedTranslationId);
            toast({
                title: 'Verification deleted',
                status: 'success',
                duration: 3000,
                isClosable: true
            });
            const updated = await fetchTranslation(selectedSurahId, selectedTranslationId);
            setAyahs(updated.ayahs);
        } catch (err) {
            toast({
                title: 'Delete failed',
                description: err.message,
                status: 'error',
                duration: 4000,
                isClosable: true
            });
            throw err; // Re-throw to let the individual component handle it
        } finally {
            setVerifying(null);
        }
    };

    const handleSearchChange = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        const filtered = surahs.filter(s =>
            s.ename.toLowerCase().includes(term.toLowerCase()) ||
            s.tname.toLowerCase().includes(term.toLowerCase()) ||
            s._id.toString().includes(term)
        );
        setFilteredSurahs(filtered);
    };

    // Split ayahs into pending and completed
    const pendingAyahs = ayahs.filter(ayah => !ayah.user_verification);
    const completedAyahs = ayahs.filter(ayah => ayah.user_verification);

    return (
        <Flex direction="row" h="100vh">
            <Sidebar isCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} />
            <Box
                pl={sidebarCollapsed ? "60px" : "250px"}
                w="full"
                overflowY="auto"
                pb={8}
                transition="padding-left 0.2s ease"
            >
                <Box p={8}>
                    <Heading mb={6}>Translation Verification</Heading>

                    {/* Surah Selector */}
                    <Box maxW="lg" mb={8}>
                        <InputGroup mb={2}>
                            <InputLeftElement pointerEvents="none">
                                <SearchIcon color="gray.300" />
                            </InputLeftElement>
                            <Input
                                placeholder="Search Surah by name or number"
                                value={searchTerm}
                                onChange={handleSearchChange}
                            />
                        </InputGroup>

                        <Select
                            placeholder={`${loading ? "Loading Surah Data ..." : "Select a Surah"}`}
                            onChange={(e) => setSelectedSurahId(e.target.value)}
                            value={selectedSurahId}
                        >
                            {filteredSurahs.map((surah) => (
                                <option key={surah._id} value={surah._id}>
                                    {surah._id}. {surah.tname} ({surah.ename})
                                </option>
                            ))}
                        </Select>
                        <Select
                            placeholder={`${loading ? "Loading Translation Data ..." : "Select Translation"}`}
                            onChange={(e) => setSelectedTranslationId(e.target.value)}
                            value={selectedTranslationId}
                        >
                            {translationInfos.map((infos) => (
                                <option key={infos._id} value={infos._id}>
                                    {infos.language} - ({infos.name})
                                </option>
                            ))}
                        </Select>
                    </Box>

                    {loading ? (
                        <Flex justify="center" align="center" minH="200px">
                            <Spinner size="xl" />
                        </Flex>
                    ) : (
                        <>
                            {selectedSurahId && (
                                <Tabs variant="enclosed" colorScheme="teal">
                                    <TabList>
                                        <Tab>Pending ({pendingAyahs.length})</Tab>
                                        <Tab>Completed ({completedAyahs.length})</Tab>
                                    </TabList>

                                    <TabPanels>
                                        {/* Pending Tab */}
                                        <TabPanel>
                                            <VStack spacing={6} align="stretch">
                                                {pendingAyahs.map((ayah) => (
                                                    <TranslationCard
                                                        key={ayah.ayah_key}
                                                        ayah={ayah}
                                                        selectedTranslationId={selectedTranslationId}
                                                        onVerify={handleVerify}
                                                        onDelete={handleDelete}
                                                        verifying={verifying}
                                                        user={user}
                                                    />
                                                ))}
                                                {pendingAyahs.length === 0 && (
                                                    <Text textAlign="center" color="gray.500">
                                                        No pending translations for verification.
                                                    </Text>
                                                )}
                                            </VStack>
                                        </TabPanel>

                                        {/* Completed Tab */}
                                        <TabPanel>
                                            <VStack spacing={6} align="stretch">
                                                {completedAyahs.map((ayah) => (
                                                    <TranslationCard
                                                        key={ayah.ayah_key}
                                                        ayah={ayah}
                                                        selectedTranslationId={selectedTranslationId}
                                                        onVerify={handleVerify}
                                                        onDelete={handleDelete}
                                                        verifying={verifying}
                                                        user={user}
                                                    />
                                                ))}
                                                {completedAyahs.length === 0 && (
                                                    <Text textAlign="center" color="gray.500">
                                                        No completed verifications yet.
                                                    </Text>
                                                )}
                                            </VStack>
                                        </TabPanel>
                                    </TabPanels>
                                </Tabs>
                            )}
                        </>
                    )}
                </Box>
            </Box>
        </Flex>
    );
};

export default TranslationVerificationPage;